import { Check, ChevronDown, Search } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

/**
 * Выпадающий отбор с поиском.
 *
 * КОПИЯ из соседа (`/claude/claude/supply/web/src/components/Combo.tsx`) —
 * кросс-репозиторного импорта в проекте нет, а два одинаковых отбора в двух
 * кабинетах обязаны вести себя одинаково: человек ходит в оба.
 *
 * Два РАСХОЖДЕНИЯ с оригиналом, и оба намеренные — сверяя копию, не верните
 * их обратно:
 *
 *   1. убран кружок цвета (`swatch`): здесь отбирают поставщиком и
 *      категорией, а не цветом ткани, и пустой кружок в каждой строке был бы
 *      колонкой ни про что;
 *   2. бледный токен (`--fg-faint`) заменён на `text-fg-muted` во всех шести
 *      местах: в тёмной теме он даёт 2.66:1 и текстом у нас запрещён. Запрет
 *      держит `src/lib/theme.test.ts` — он ищет класс по всем `.tsx`, поэтому
 *      здесь тот класс не назван и дословно.
 *
 * Заменяет нативный `<select>` не ради красоты: браузер рисует его список
 * СВОИМ стилем — белым на тёмной теме и без единого нашего токена, — и в
 * списке из шестидесяти трёх поставщиков нет поиска. Дальше выбора глазами по
 * алфавиту такой список не ведёт.
 *
 * Поле поиска стоит ВСЕГДА и получает фокус на открытии: «щёлкнул — начал
 * писать» это одно движение, тогда как «щёлкнул — нашёл глазами строку поиска
 * — щёлкнул в неё» три. Список из четырёх пунктов от этого не страдает: там
 * поиск просто не нужен, и его никто не трогает.
 *
 * Значение — строка, как в адресе: этот компонент кладёт выбор прямо в
 * `?supplier=`, и число здесь стало бы лишним переводом туда-обратно.
 */

export type ComboOption = {
  value: string;
  label: string;
  /** Правая колонка строки: счётчик, объём — то, чем выбирают из равных. */
  meta?: string;
  /** Подсказка строки и материал для поиска: полный путь категории, к примеру. */
  title?: string;
};

export function Combo({
  label,
  value,
  options,
  onChange,
  placeholder = "поиск",
  empty = "ничего не найдено",
  width = 210,
}: {
  label: string;
  value: string;
  options: ComboOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  empty?: string;
  width?: number;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [cursor, setCursor] = useState(0);
  const boxRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const current = options.find((o) => o.value === value);
  const shown = useMemo(() => {
    const needle = draft.trim().toLowerCase();
    if (!needle) return options;
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(needle) ||
        (o.title ?? "").toLowerCase().includes(needle),
    );
  }, [options, draft]);

  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
    setCursor(
      Math.max(
        0,
        shown.findIndex((o) => o.value === value),
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Клик мимо закрывает список. Слушаем mousedown, а не click: щелчок по
  // соседней кнопке отбора должен и закрыть список, и сработать сам.
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (!boxRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, [open]);

  // Курсор клавиатуры обязан оставаться видимым: в длинном списке «вниз» без
  // прокрутки уводит выбор за край экрана.
  useEffect(() => {
    if (!open) return;
    const row = listRef.current?.children[cursor] as HTMLElement | undefined;
    row?.scrollIntoView({ block: "nearest" });
  }, [cursor, open]);

  const pick = (next: string) => {
    onChange(next);
    setOpen(false);
    setDraft("");
  };

  const keys = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setOpen(false);
      setDraft("");
      return;
    }
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const step = e.key === "ArrowDown" ? 1 : -1;
      setCursor((c) => Math.min(shown.length - 1, Math.max(0, c + step)));
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      const row = shown[cursor];
      if (row) pick(row.value);
    }
  };

  return (
    <div ref={boxRef} className="relative" data-annot={`отбор ${label}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={current?.title ?? label}
        className={
          "flex h-8 items-center overflow-hidden rounded-[var(--radius-ui)] border bg-bg text-[13px] " +
          (open
            ? "border-border-strong"
            : "border-border hover:border-border-strong")
        }
        style={{ maxWidth: width }}
      >
        <span className="flex h-full flex-none items-center whitespace-nowrap border-r border-border bg-bg-subtle px-2 text-[11.5px] text-fg-muted">
          {label}
        </span>
        <span className="truncate px-2 text-fg">{current?.label ?? "—"}</span>
        <ChevronDown
          size={13}
          className="mr-1.5 ml-auto flex-none text-fg-muted"
        />
      </button>

      {open ? (
        <div
          className="absolute top-[34px] left-0 z-30 min-w-full overflow-hidden rounded-[var(--radius-card)] border border-border bg-bg-elevated shadow-lg"
          style={{ width: Math.max(width, 240) }}
        >
          <div className="flex items-center gap-1.5 border-b border-border px-2">
            <Search size={12} className="flex-none text-fg-muted" />
            <input
              ref={inputRef}
              value={draft}
              onChange={(e) => {
                setDraft(e.target.value);
                setCursor(0);
              }}
              onKeyDown={keys}
              placeholder={placeholder}
              className="h-8 w-full bg-transparent text-[13px] text-fg outline-none placeholder:text-fg-muted"
            />
          </div>
          <div ref={listRef} className="max-h-[280px] overflow-auto py-1">
            {shown.length === 0 ? (
              <div className="px-2.5 py-2 text-[12.5px] text-fg-muted">
                {empty}
              </div>
            ) : (
              shown.map((o, i) => (
                <button
                  key={o.value}
                  type="button"
                  title={o.title}
                  onMouseEnter={() => setCursor(i)}
                  onClick={() => pick(o.value)}
                  className={
                    "flex w-full items-center gap-1.5 px-2.5 py-1.5 text-left text-[12.5px] " +
                    (i === cursor ? "bg-bg-hover " : "") +
                    (o.value === value ? "text-accent-text" : "text-fg")
                  }
                >
                  <span className="truncate">{o.label}</span>
                  {o.meta ? (
                    <span className="tnum ml-auto flex-none pl-2 text-[11.5px] text-fg-muted">
                      {o.meta}
                    </span>
                  ) : null}
                  {o.value === value ? (
                    <Check
                      size={12}
                      className={o.meta ? "flex-none" : "ml-auto flex-none"}
                    />
                  ) : null}
                </button>
              ))
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
